import {Card, Flag} from 'platform/components';
import {Box, HStack, Link, Show, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {AuctionVehicleDetailResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {CATALOGUE_MAKE_KEY_PREFIX} from '../../../constants/CATALOGUE_MAKE_KEY_PREFIX';
import {DEFAULT_CURRENCY_CODE} from '../../../constants/DEFAULT_CURRENCY_CODE';
import {useApiEnums} from '../../../hooks/useApiEnums';
import {useAuctionEnumLookup} from '../../../hooks/useAuctionEnumLookup';
import {CarStatusFilter} from '../../../pages/AllOffers/types/CarStatusFilter';
import {buildAuctionVehicleTitle} from '../../../utils/buildAuctionVehicleTitle';
import {isHiddenAuctionType} from '../../../utils/isHiddenAuctionType';
import {isUnifiedSourcingEnabled} from '../../../utils/isUnifiedSourcingEnabled';
import {resolveAuctionEnumLabel} from '../../../utils/resolveAuctionEnumLabel';
import {AuctionTypeBadge} from '../../AuctionTypeBadge/AuctionTypeBadge';
import {DescriptionCard} from '../../ListingPreview/components/DescriptionCard';
import {FeatureList} from '../../ListingPreview/components/FeatureList';
import {ListingGallery} from '../../ListingPreview/components/ListingGallery';
import {ListingTitle} from '../../ListingPreview/components/ListingTitle';
import {PriceCard} from '../../ListingPreview/components/PriceCard';
import {SellerCard} from '../../ListingPreview/components/SellerCard';
import {buildPriceBreakdown} from '../../ListingPreview/utils/buildPriceBreakdown';
import {buildSpecificationItems} from '../../ListingPreview/utils/buildSpecificationItems';
import {getPremiumFeatures} from '../../ListingPreview/utils/getPremiumFeatures';
import {toSafeExternalUrl} from '../../ListingPreview/utils/toSafeExternalUrl';
import {getSellerMapUrl} from '../../VehiclePreview/utils/getSellerMapUrl';
import {useLiveAuction} from '../hooks/useLiveAuction';
import {buildLocationDetail, buildLocationName} from '../utils/buildLocationName';
import {formatVatRate} from '../utils/formatVatRate';
import {AuctionCountdownCard} from './AuctionCountdownCard';
import {AuctionIdentifiers} from './AuctionIdentifiers';
import {AuctionServerLogo} from './AuctionServerLogo';
import {SealedAuctionPriceCard} from './SealedAuctionPriceCard';

interface AuctionPreviewRedesignProps extends TestIdProps {
  auction: AuctionVehicleDetailResponseBody;
}

/** Auction preview with countdown and identifiers; content reflows with the panel width. */
export function AuctionPreviewRedesign(props: AuctionPreviewRedesignProps) {
  const enhancedSurfaces = isUnifiedSourcingEnabled();
  // Refreshed in place once the lot passes its listed end, so the countdown follows the auction.
  const auction = useLiveAuction(props.auction);
  const auctionVehicle = auction.auctionVehicle;
  const isHiddenType = isHiddenAuctionType(auctionVehicle.auctionType);
  const enumLookup = useAuctionEnumLookup();
  const formatNumber = useFormatNumber();
  const {getEnum} = useApiEnums();

  const resolve = (value: string | Nullish) => resolveAuctionEnumLabel(value, enumLookup);

  const countryName =
    getEnum('country').find(({key}) => key === auctionVehicle.sellerCountry)?.label ??
    auctionVehicle.sellerCountry;

  // Alpha sends the catalogue key (MAKE_BMW); a plain make name is mapped back to its key.
  const makeKey = auctionVehicle.make?.startsWith(CATALOGUE_MAKE_KEY_PREFIX)
    ? auctionVehicle.make
    : Array.from(enumLookup).find(
        ([key, label]) => key.startsWith(CATALOGUE_MAKE_KEY_PREFIX) && label === auctionVehicle.make
      )?.[0];

  const title = buildAuctionVehicleTitle(
    auctionVehicle.make,
    auctionVehicle.model,
    auctionVehicle.adTitle,
    enumLookup
  );

  const specifications = buildSpecificationItems({
    year: isNotNil(auctionVehicle.year) ? auctionVehicle.year.toString() : null,
    power: isNotNil(auctionVehicle.power)
      ? `${auctionVehicle.power} ${i18n.t('general.metric.kW')}`
      : null,
    driveType: resolve(auctionVehicle.driveType),
    mileage: isNotNil(auctionVehicle.mileage)
      ? `${formatNumber(auctionVehicle.mileage)} ${i18n.t('general.metric.km')}`
      : null,
    transmission: resolve(auctionVehicle.transmission),
    fuelType: resolve(auctionVehicle.fuelType),
    carStyle: resolve(auctionVehicle.carStyle),
  });

  const priceBreakdown = buildPriceBreakdown({
    price: auctionVehicle.priceActual,
    currency: auctionVehicle.currency ?? DEFAULT_CURRENCY_CODE,
    originalPrice: auctionVehicle.originalPriceActual,
    originalCurrency: auctionVehicle.originalCurrency,
    vatRate: auctionVehicle.vatRate,
    isVatDeductible: auctionVehicle.vatReclaimable,
  });

  const vatRate = auctionVehicle.vatRate;
  // Without a seller country the note leaves out the country instead of ending in "()".
  const vatNote = match([
    auctionVehicle.vatReclaimable === true && isNotNil(vatRate),
    isNotNilOrEmpty(countryName),
  ])
    .returnType<string | null>()
    .with([false, true], [false, false], () => null)
    .with([true, true], () =>
      i18n
        .t('entity.auction.labels.inclVat', {
          vatRate: formatVatRate(vatRate ?? 0),
          country: countryName,
        })
        .trim()
    )
    .with([true, false], () =>
      i18n.t('entity.sourcing.vehiclePreview.labels.vatIncluded', {
        vat: formatVatRate(vatRate ?? 0),
      })
    )
    .exhaustive();

  const seller = auctionVehicle.seller;
  const hasPickupPoint = isNotNil(seller) && (isNotNil(seller.address) || isNotNil(seller.city));
  const pickupMapUrl = isNil(seller)
    ? null
    : getSellerMapUrl({
        name: null,
        type: null,
        address: seller.address ?? null,
        city: seller.city ?? null,
        zip: seller.zip ?? null,
        email: null,
        phone: null,
        rating: null,
        country: isNotNil(auctionVehicle.sellerCountry)
          ? {key: auctionVehicle.sellerCountry, translation: countryName ?? ''}
          : null,
      });

  const premiumFeatures = getPremiumFeatures(
    auction.features,
    isNotNil(auctionVehicle.interiorMaterial)
      ? {
          key: auctionVehicle.interiorMaterial,
          translation: resolve(auctionVehicle.interiorMaterial) ?? auctionVehicle.interiorMaterial,
        }
      : null
  );

  const features = (auctionVehicle.features ?? []).map((feature) => ({
    key: feature,
    label: resolve(feature) ?? feature,
  }));

  const header = (
    <HStack justify="space-between" align="center" spacing={2} wrap>
      {isNotNil(seller) ? (
        <AuctionServerLogo
          server={seller.id ?? ''}
          serverName={seller.name ?? undefined}
          data-testid={suffixTestId('serverLogo', props)}
        />
      ) : (
        // The portal name stands in for a missing logo; an empty box keeps the link on the right.
        <Box>
          <Show when={auctionVehicle.auctionName}>
            {(auctionName) => (
              <Flag label={auctionName} data-testid={suffixTestId('portalBadge', props)} />
            )}
          </Show>
        </Box>
      )}
      <Show when={toSafeExternalUrl(auctionVehicle.url)}>
        {(url) => (
          <Link
            title={i18n.t('entity.auction.labels.openOriginalUrl')}
            rightIcon="action/open_in_new"
            size="small"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={suffixTestId('openPortalLink', props)}
          />
        )}
      </Show>
    </HStack>
  );

  return (
    <VStack spacing={3}>
      <Show when={!enhancedSurfaces}>{header}</Show>
      <Card>
        <VStack spacing={4}>
          <Show when={enhancedSurfaces}>{header}</Show>
          <VStack>
            <ListingGallery
              key={auctionVehicle.adId ?? auctionVehicle.pk ?? undefined}
              pictureUrls={auctionVehicle.picturesUrl ?? []}
              thumbnailUrl={auctionVehicle.thumbnailUrl}
              data-testid={suffixTestId('gallery', props)}
            />
          </VStack>
          <ListingTitle
            title={title}
            make={makeKey}
            specifications={specifications}
            data-testid={suffixTestId('title', props)}
          />
          <AuctionCountdownCard
            carEndTime={auctionVehicle.carEndTime}
            isEnded={auctionVehicle.carStatus === CarStatusFilter.ENDED}
            data-testid={suffixTestId('countdown', props)}
          />
          <AuctionTypeBadge
            auctionType={auctionVehicle.auctionType}
            showDescription
            data-testid={suffixTestId('auctionType', props)}
          />
          {/* A lot without a bid yet still shows the card, with a placeholder price. */}
          <Show when={isHiddenType}>
            <SealedAuctionPriceCard
              auctionVehicle={auctionVehicle}
              vatNote={vatNote}
              data-testid={suffixTestId('price', props)}
            />
          </Show>
          <Show when={!isHiddenType}>
            <PriceCard
              label={i18n.t('entity.auction.labels.currentBid')}
              breakdown={priceBreakdown}
              vatNote={vatNote}
              isVatDeductible={auctionVehicle.vatReclaimable}
              data-testid={suffixTestId('price', props)}
            />
          </Show>
          <Box borderTop="1px solid" borderColor="general.separator" paddingTop={4}>
            <AuctionIdentifiers
              auctionVehicle={auctionVehicle}
              data-testid={suffixTestId('identifiers', props)}
            />
          </Box>
        </VStack>
      </Card>
      <Show when={hasPickupPoint ? seller : null}>
        {(pickupPoint) => (
          <SellerCard
            title={i18n.t('entity.auction.labels.pickupPoint').trim()}
            avatarIcon="communication/location_on"
            name={buildLocationName(pickupPoint.address, pickupPoint.city)}
            countryCode={auctionVehicle.sellerCountry}
            address={buildLocationDetail(pickupPoint.zip, countryName)}
            mapUrl={pickupMapUrl}
            data-testid={suffixTestId('pickupPoint', props)}
          />
        )}
      </Show>
      <FeatureList
        key={`features-${auctionVehicle.adId ?? auctionVehicle.pk ?? ''}`}
        features={features}
        premiumFeatures={premiumFeatures}
        data-testid={suffixTestId('features', props)}
      />
      <DescriptionCard
        key={`description-${auctionVehicle.adId ?? auctionVehicle.pk ?? ''}`}
        description={auctionVehicle.description}
        data-testid={suffixTestId('description', props)}
      />
    </VStack>
  );
}
